/**
 * Async Database Query Manager
 * Gerencia requisições ao banco de dados de forma não-bloqueante
 */

class AsyncDatabaseManager {
    constructor(baseUrl = '/api') {
        this.baseUrl = baseUrl;
        this.cache = new Map();
        this.pendingRequests = new Map();
    }

    /**
     * Fazer requisição assíncrona com deduplicação
     */
    async fetch(endpoint, options = {}) {
        const {
            cache = true,
            cacheDuration = 3600000, 
            method = 'GET',
            body = null,
            signal = null
        } = options;

        const cacheKey = `${method}:${endpoint}`;

        // Verificar cache
        if (cache && this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < cacheDuration) {
                return Promise.resolve(cached.data);
            }
        }


        if (this.pendingRequests.has(cacheKey)) {
            return this.pendingRequests.get(cacheKey);
        }

        const requestPromise = (async () => {
            try {
                const url = this.buildUrl(endpoint);
                const response = await fetch(url, {
                    method,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        ...options.headers
                    },
                    body: body ? JSON.stringify(body) : null,
                    signal
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const data = await response.json();
                if (cache) {
                    this.cache.set(cacheKey, {
                        data,
                        timestamp: Date.now()
                    });
                }

                return data;
            } finally {
                this.pendingRequests.delete(cacheKey);
            }
        })();

        // Armazenar requisição pendente
        this.pendingRequests.set(cacheKey, requestPromise);

        return requestPromise;
    }

    /**
     * GET request
     */
    get(endpoint, options = {}) {
        return this.fetch(endpoint, { ...options, method: 'GET' });
    }

    /**
     * POST request
     */
    post(endpoint, body, options = {}) {
        return this.fetch(endpoint, { ...options, method: 'POST', body });
    }

    /**
     * PUT request
     */
    put(endpoint, body, options = {}) {
        return this.fetch(endpoint, { ...options, method: 'PUT', body });
    }

    /**
     * DELETE request
     */
    delete(endpoint, options = {}) {
        return this.fetch(endpoint, { ...options, method: 'DELETE' });
    }

    /**
     * Limpar cache
     */
    clearCache(pattern = null) {
        if (pattern) {
            for (const key of this.cache.keys()) {
                if (key.includes(pattern)) {
                    this.cache.delete(key);
                }
            }
        } else {
            this.cache.clear();
        }
    }

    /**
     * Carregar múltiplas requisições em paralelo
     */
    async loadMany(endpoints, options = {}) {
        const promises = endpoints.map(endpoint =>
            this.get(endpoint, options)
        );
        return Promise.all(promises);
    }

    /**
     * Carregar com retry automático
     */
    async fetchWithRetry(endpoint, options = {}, maxRetries = 3) {
        const { delay = 1000, ...rest } = options;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                return await this.fetch(endpoint, rest);
            } catch (error) {
                if (attempt === maxRetries - 1) throw error;
                await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
            }
        }
    }

    /**
     * Cancelar requisições pendentes
     */
    abortAll() {
        this.pendingRequests.forEach((promise) => {
            // As requisições serão canceladas se tiverem AbortController
        });
        this.pendingRequests.clear();
    }

    /**
     * Verificar status de cache
     */
    getCacheStats() {
        return {
            cacheSize: this.cache.size,
            pendingRequests: this.pendingRequests.size,
            cacheKeys: Array.from(this.cache.keys())
        };
    }

    /**
     * Montar URL final da requisição evitando /api/api
     */
    buildUrl(endpoint = '') {
        if (typeof endpoint !== 'string') {
            endpoint = String(endpoint ?? '');
        }

        if (/^https?:\/\//i.test(endpoint)) {
            return endpoint;
        }

        const normalizedBase = String(this.baseUrl || '').replace(/\/$/, '');
        const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

        if (
            normalizedBase &&
            normalizedEndpoint.toLowerCase().startsWith(`${normalizedBase.toLowerCase()}/`)
        ) {
            return normalizedEndpoint;
        }

        return `${normalizedBase}${normalizedEndpoint}`;
    }
}

// Inicializar globalmente
window.dbManager = new AsyncDatabaseManager();
